import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import { HOST, API } from '@/utils/http'
import { v4 as uuidv4 } from 'uuid'
import { centroid } from '@turf/turf'
import { toast } from '@/components/ui/toast/use-toast'
import { secondsDiff } from '@/utils/datetime'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'

type FeatureCollection = { type: 'FeatureCollection', features: any[] }

const collection = (features: any[]): FeatureCollection => markRaw({ type: 'FeatureCollection', features })
const featureSig = (f: any) => `${f.properties?.code}|${JSON.stringify(f.properties)}|${f.geometry ? JSON.stringify(f.geometry) : '-'}`
const thumbUrlOf = (resources: any[] | undefined) =>
  resources?.find(r => r.url?.includes('small'))?.url || resources?.find(r => r.url?.includes('thumbnail'))?.url


const featureToCoordinates = (feature: any): number[] | null => {
  const geometry = feature?.geometry
  if (geometry?.type === 'Point' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates
  }
  const props = feature?.properties || {}
  if (props.center_lng != null && props.center_lat != null) {
    return [props.center_lng, props.center_lat]
  }
  if (geometry) {
    try {
      return centroid(geometry).geometry.coordinates
    } catch (e) {
      return null
    }
  }
  return null
}

// Transient geometry rendered for an externally-opened feature (e.g. a daily-review
// report link), mirroring the MapSearch preview pattern. Independent of project layers.
const TRANSIENT_SOURCE = 'geo-popup-feature'

const removeTransientGeometry = (map: any) => {
  if (!map) return
  if (map.getLayer(`${TRANSIENT_SOURCE}-fill`)) map.removeLayer(`${TRANSIENT_SOURCE}-fill`)
  if (map.getLayer(`${TRANSIENT_SOURCE}-line`)) map.removeLayer(`${TRANSIENT_SOURCE}-line`)
  if (map.getLayer(`${TRANSIENT_SOURCE}-circle`)) map.removeLayer(`${TRANSIENT_SOURCE}-circle`)
  if (map.getSource(TRANSIENT_SOURCE)) map.removeSource(TRANSIENT_SOURCE)
}

const addTransientGeometry = (map: any, feature: any) => {
  if (!map || !feature?.geometry) return
  try {
    removeTransientGeometry(map)
    map.addSource(TRANSIENT_SOURCE, { type: 'geojson', data: feature })
    const type = feature.geometry.type
    const color = ['coalesce', ['get', 'color'], '#0059ff']
    if (type === 'Polygon' || type === 'MultiPolygon') {
      map.addLayer({ id: `${TRANSIENT_SOURCE}-fill`, type: 'fill', source: TRANSIENT_SOURCE, paint: { 'fill-color': color, 'fill-opacity': 0.5 } })
      map.addLayer({ id: `${TRANSIENT_SOURCE}-line`, type: 'line', source: TRANSIENT_SOURCE, paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.9 } })
    } else if (type === 'LineString' || type === 'MultiLineString') {
      map.addLayer({ id: `${TRANSIENT_SOURCE}-line`, type: 'line', source: TRANSIENT_SOURCE, paint: { 'line-color': color, 'line-width': 3, 'line-opacity': 0.9 } })
    } else {
      map.addLayer({ id: `${TRANSIENT_SOURCE}-circle`, type: 'circle', source: TRANSIENT_SOURCE, paint: { 'circle-color': color, 'circle-radius': 8, 'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff' } })
    }
  } catch (err: any) {
    const msg = err?.message ?? ''
    const transient = msg.includes('Style is not done loading') || msg.includes('Source') || msg.includes('style')
    if (!transient) throw err
    // Style still processing (e.g. link clicked mid style-load/reload) — retry on next styledata tick
    map.once('styledata', () => addTransientGeometry(map, feature))
  }
}


interface GeoState {
  // The live Mapbox map and its draw control (see components/map/).
  map: any
  draw: any
  mapReady: boolean
  features: FeatureCollection
  feedFeatures: FeatureCollection
  featureSigs: string[]
  featureCache: Record<string, any>
  featureCacheKey: string | null
  featureFetchStamp: number
  currentFeature: any
  planetFeaturesEnabled: boolean
  beacons: any[]
  files: any
  reportTemplates: any[] | null
  allTemplates: any[] | null
  systemTemplatesLibrary: any[] | null
  isFeatureCreated: boolean
  // Heavy feature geometry cache - loaded on demand for features with >5000 points
  loadedHeavyGeometries: Record<string, any>
  heavyFeatureLoading: Record<string, boolean>
  // Feature visibility state - tracks which features are visible on map
  // Key: feature code, Value: boolean (true = visible, false = hidden)
  featureVisibility: Record<string, boolean>
  // Project-level filters (shared between feed and map)
  projectFilters: any
  // Globally-driven feature popup (e.g. opened from a daily-review report link).
  // counter forces FeaturePopup to re-fetch even for the same feature.
  featurePopup: { feature: any, coordinates: number[] | null, counter: number, visible: boolean }
}

export const useGeoStore = defineStore('geo', {
  state: (): GeoState => ({
    map: null,
    draw: null,
    mapReady: false,
    features: {
      type: 'FeatureCollection',
      features: []
    },
    feedFeatures: {
      type: 'FeatureCollection',
      features: []
    },
    featureSigs: [],
    featureCache: markRaw({}),
    featureCacheKey: null,
    featureFetchStamp: 0,
    currentFeature: null,
    planetFeaturesEnabled: false,
    beacons: [],
    files: null,
    reportTemplates: null,
    allTemplates: null,
    systemTemplatesLibrary: null,
    isFeatureCreated: false,
    loadedHeavyGeometries: {},
    heavyFeatureLoading: {},
    featureVisibility: {},
    projectFilters: null,
    featurePopup: { feature: null, coordinates: null, counter: 0, visible: false },
  }),

  getters: {
    // Derive PlanetScope access from org member key settings; default to allowed when unknown
    getIsPlanetscopeAccess: (state): boolean => {
      if (state.planetFeaturesEnabled === true) {
        return true
      }

      const org = useOrgStore()
      const currentUserId = useUserStore().member?.member_id
      const currentOrg = org.current
      const orgMembers = org.currentMembers

      if (!currentUserId || !currentOrg) {
        // If we can't determine, don't block user
        return true
      }

      if (Array.isArray(orgMembers) && orgMembers.length) {
        const me = orgMembers.find((m: any) => m.member_id === currentUserId)
        if (me && Array.isArray(me.key_settings) && me.key_settings.length) {
          const ks = me.key_settings
          const match = ks.find((k: any) => {
            const name = (k?.name || '').toLowerCase()
            return name === 'planetscope' || name === 'planet scope'
          })
          if (match && typeof match.enabled === 'boolean') {
            return !!match.enabled
          }
        }
      }

      return true
    },

    isHeavyFeatureLoading: state => (code: string) => state.heavyFeatureLoading[code] === true,

    isHeavyFeatureLoaded: state => (code: string) => !!state.loadedHeavyGeometries[code],

    getFeatureVisibility: state => (featureCode: string) => {
      // Default to true (visible) if not explicitly set
      return state.featureVisibility[featureCode] !== false
    },

    getVisibleFeatures: (state): FeatureCollection => {
      if (!state.features || !state.features.features) {
        return { type: 'FeatureCollection', features: [] }
      }

      const visibleFeatures = state.features.features.filter(feature => {
        const code = feature.properties?.code
        // Default to visible if not explicitly hidden
        return state.featureVisibility[code] !== false
      })

      return markRaw({
        type: 'FeatureCollection',
        features: visibleFeatures
      })
    },
  },

  actions: {
    // --- Map handles -------------------------------------------------------------

    setMap(map: any) {
      this.map = map
    },
    setDraw(draw: any) {
      this.draw = draw
    },
    markMapReady() {
      this.mapReady = true
    },

    // --- Feature popup -----------------------------------------------------------

    // Fetch a feature by code and open its popup on the persistent map (used by
    // daily-review report links). Independent of which project layer is mounted.
    async openFeaturePopupByCode(code: string) {
      if (!code) return
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/geo/feature/${code}`
        })
        const feature = response.data
        const coordinates = featureToCoordinates(feature)

        if (!coordinates) {
          toast({ title: "Can't locate this report on the map", variant: 'destructive' })
          return
        }

        const map = this.map
        if (map) {
          addTransientGeometry(map, feature)
          const zoom = Math.max(map.getZoom?.() || 0, 14)
          map.flyTo({ center: coordinates, zoom, duration: 800 })
        }

        this.featurePopup = {
          feature,
          coordinates,
          counter: this.featurePopup.counter + 1,
          visible: true,
        }
      } catch (e) {
        toast({ title: "Can't open report on the map", variant: 'destructive' })
      }
    },

    // Close the externally-opened feature popup and remove its transient geometry.
    closeFeaturePopup() {
      removeTransientGeometry(this.map)
      this.featurePopup = {
        ...this.featurePopup,
        feature: null,
        coordinates: null,
        visible: false,
      }
    },

    // --- Features ----------------------------------------------------------------

    mergeFeatures({ features, cacheKey, reset = false }: { features: FeatureCollection, cacheKey?: string | null, reset?: boolean }) {
      if (reset || cacheKey == null || cacheKey !== this.featureCacheKey) {
        this.featureCache = markRaw({})
        this.featureCacheKey = cacheKey ?? null
      }
      const cache = this.featureCache

      for (const f of features.features) {
        const thumbUrl = thumbUrlOf(f.properties.resources)
        if (thumbUrl) {
          f.properties.thumbUrl = thumbUrl
        }
        const code = f.properties.code
        if (f.properties.is_heavy && !f.geometry && this.loadedHeavyGeometries[code]) {
          f.geometry = this.loadedHeavyGeometries[code]
        }
        cache[code] = f
      }

      const newFeatures = Object.values(cache)
      const newSigs = newFeatures.map(featureSig)
      const oldSigs = this.featureSigs
      if (newSigs.length === oldSigs.length && newSigs.every((s, i) => s === oldSigs[i])) {
        return
      }

      this.featureSigs = newSigs
      this.features = collection(newFeatures)
    },

    invalidateFeatureFetch() {
      this.featureFetchStamp++
    },

    setFeedFeatures({ features }: { features: FeatureCollection }) {
      const seen = new Set()
      const uniqueFeatures = features.features.filter(f => {
        const code = f.properties?.code
        if (!code || seen.has(code)) return false
        seen.add(code)
        return true
      })

      this.feedFeatures = collection(uniqueFeatures)
    },

    setCurrentFeature({ feature }: { feature: any }) {
      this.currentFeature = feature
    },

    setFiles(files: any) {
      this.files = files
    },

    setFeatureCreated(created: boolean) {
      this.isFeatureCreated = created
    },

    setProjectFilters(filters: any) {
      this.projectFilters = filters
    },

    toggleFeatureVisibility(featureCode: string) {
      if (this.featureVisibility[featureCode] === undefined || this.featureVisibility[featureCode] === true) {
        // If not set or visible, hide it
        this.featureVisibility = { ...this.featureVisibility, [featureCode]: false }
      } else {
        // If hidden, show it
        this.featureVisibility = { ...this.featureVisibility, [featureCode]: true }
      }
    },

    setHeavyFeatureGeometry({ code, geometry }: { code: string, geometry: any }) {
      this.loadedHeavyGeometries = { ...this.loadedHeavyGeometries, [code]: geometry }

      if (this.featureCache[code]) {
        this.featureCache[code] = { ...this.featureCache[code], geometry }
      }
      const featureIndex = this.features.features.findIndex(f => f.properties.code === code)
      if (featureIndex !== -1) {
        const updatedFeatures = [...this.features.features]
        updatedFeatures[featureIndex] = { ...updatedFeatures[featureIndex], geometry }
        this.featureSigs = updatedFeatures.map(featureSig)
        this.features = collection(updatedFeatures)
      }
    },

    async fetchMapFeatures(payload: { org_id: number | string, cacheKey?: string | null, reset?: boolean, isStale?: () => boolean } & Record<string, any>) {
      try {
        const { org_id, cacheKey, reset, isStale, ...params } = payload
        const response = await API({
          method: 'GET',
          url: `${HOST}/organization/${org_id}/features`,
          params
        })
        if (isStale?.()) return false
        this.mergeFeatures({ features: response.data, cacheKey, reset })
        return true
      } catch (e: any) {
        if (!e.response) {
          toast({ title: "Can't fetch features", variant: 'destructive' })
          return false
        }

        if (e.response.status === 403) {
          toast({ title: "You don't have permission to access this project", variant: 'destructive' })
          return false
        }
        throw new Error(e)
      }
    },

    async fetchFeature(code: string) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/geo/feature/${code}`
        })

        this.currentFeature = response.data
      } catch (e: any) {
        if (!e.response) {
          toast({ title: "Can't fetch report", variant: 'destructive' })
          return
        }

        if (e.response.status === 403) {
          toast({ title: "You don't have permission to access this report", variant: 'destructive' })
        }

        if (e.response.status === 404) {
          toast({ title: "Report not found", variant: 'destructive' })
        }
      }
    },

    async loadHeavyFeatureGeometry(code: string) {
      try {
        this.heavyFeatureLoading = { ...this.heavyFeatureLoading, [code]: true }

        const response = await API({
          method: 'GET',
          url: `${HOST}/geo/feature/${code}`
        })

        const geometry = response.data?.geometry
        if (geometry) {
          this.setHeavyFeatureGeometry({ code, geometry })
        }

        this.heavyFeatureLoading = { ...this.heavyFeatureLoading, [code]: false }
      } catch (e) {
        this.heavyFeatureLoading = { ...this.heavyFeatureLoading, [code]: false }
        toast({ title: "Can't load feature geometry", variant: 'destructive' })
      }
    },

    async saveResource(payload: { code: string, uuid: string, file: any }) {
      try {
        await API({
          headers: {'content-type': 'multipart/form-data'},
          method: 'POST',
          url: `${HOST}/geo/feature/${payload.code}/resource`,
          params: {
            uuid: payload.uuid
          },
          data: {
            file: payload.file
          }
        })
      } catch (e: any) {
        throw new Error(e)
      }
    },

    async saveFeature(request: { project_id: number | string, monitor_area?: boolean, feature: any, files?: any[] | null }) {
      try {
        const response = await API({
          method: 'POST',
          url: `${HOST}/geo/feature?project_id=${request.project_id}${request.monitor_area ? '&monitor_area=true' : ''}`,
          data: request.feature
        })

        const feature = response.data
        // Set when the feature was saved but its "Monitor area" switch could not
        // be honored (project monitoring off, cap reached, ...).
        feature.monitoringWarning = response.headers['x-monitoring-warning'] || null
        const projectStr = `project-${request.project_id}-`

        if (this.map.getSource(`${projectStr}reports_points`)) {
          if (feature.geometry.type === 'Point') {
            this.map.getSource(`${projectStr}reports_points`).setData(feature)
          } else {
            this.map.getSource(`${projectStr}reports_mixed`).setData(feature)
          }
        }

        if (request.files) {
          for (const file of request.files) {
            await this.saveResource({
              code: feature.properties.code,
              file: file,
              uuid: uuidv4()
            })
          }
        }

        this.invalidateFeatureFetch()
        return feature
      } catch (e: any) {
        throw new Error(e)
      }
    },

    async updateFeature(payload: { project_id: number | string, feature: any }) {
      try {
        const response = await API({
          method: 'PUT',
          url: `${HOST}/geo/feature/${payload.feature.properties.code}`,
          data: payload.feature
        })

        const feature = response.data
        const projectStr = `project-${payload.project_id}-`

        if (this.map.getSource(`${projectStr}reports_points`)) {
          if (feature.geometry.type === 'Point') {
            this.map.getSource(`${projectStr}reports_points`).setData(feature)
          } else {
            this.map.getSource(`${projectStr}reports_mixed`).setData(feature)
          }
        }

        this.currentFeature = feature
        this.invalidateFeatureFetch()
        return feature
      } catch (e: any) {
        throw new Error(e)
      }
    },

    async deleteFeature(code: string) {
      try {
        await API({
          method: 'DELETE',
          url: `${HOST}/geo/feature/${code}`
        })

        delete this.featureCache[code]
        const remaining = this.features.features.filter(f => f.properties.code !== code)
        this.featureSigs = remaining.map(featureSig)
        this.features = collection(remaining)
        this.feedFeatures = collection(this.feedFeatures.features.filter(f => f.properties.code !== code))
      } catch(e: any) {
        throw new Error(e)
      }
    },

    // --- Beacons -----------------------------------------------------------------

    nextBeacon(next_beacon: any) {
      if (!this.beacons || this.beacons.find(b => b.properties.member_id === next_beacon.properties.member_id)) {
        const index = this.beacons.findIndex(b => b.properties.member_id === next_beacon.properties.member_id)
        this.beacons.splice(index, 1, next_beacon)
      } else {
        this.beacons.push(next_beacon)
      }
    },
    removeBeacon(memberId: number) {
      const index = this.beacons.findIndex(beacon => beacon.properties.member_id === memberId)
      this.beacons.splice(index, 1)
    },
    cleanupBeacons() {
      const day = 24 * 60 * 60 * 1000
      this.beacons = this.beacons.filter(beacon => secondsDiff(new Date(), beacon.properties.rel_ts) <= day)
      console.log('WS: cleanup beacons')
    },

    async fetchBeacons(payload: { project_id: number | string }) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/geo/beacons/${payload.project_id}`
        })

        for (const beacon of response.data) {
          this.nextBeacon(beacon)
        }
      } catch (e) {
        // Beacons are best-effort.
      }
    },

    // --- Report templates --------------------------------------------------------

    addReportTemplate(template: any) {
      this.reportTemplates!.push(template)
    },

    setReportTemplates(templates: any[] | null) {
      this.reportTemplates = templates
    },

    setSystemTemplatesLibrary(templates: any[] | null) {
      this.systemTemplatesLibrary = templates
    },

    replaceReportTemplate(template: any) {
      const index = this.reportTemplates!.findIndex(t => t.id === template.id)
      this.reportTemplates!.splice(index, 1, template)
    },

    removeReportTemplate(template: any) {
      this.reportTemplates = this.reportTemplates!.filter(t => t.id !== template.id)
    },

    async generateReportTemplate({ projectId, prompt, generate_fields, generate_summary_prompt }: { projectId: number | string, prompt: string, generate_fields?: boolean, generate_summary_prompt?: boolean }) {
      try {
        const response = await API({
          method: 'POST',
          url: `${HOST}/project/${projectId}/templates/generate`,
          data: { prompt, generate_fields, generate_summary_prompt }
        })
        return response.data
      } catch (e: any) {
        const detail = e?.response?.data?.detail
        toast({ title: detail || "Can't generate template", variant: 'destructive' })
        throw e
      }
    },

    async createReportTemplate(payload: { template: any, icon?: any }) {
      try {
        const { template, icon } = payload

        // Use admin endpoint if project_id is undefined, otherwise use project endpoint
        const isAdminContext = template.project_id === undefined
        const createUrl = isAdminContext
          ? `${HOST}/admin/templates`
          : `${HOST}/project/${template.project_id}/templates`

        const response = await API({
          method: 'POST',
          url: createUrl,
          data: template
        })

        const newTemplate = response.data

        if (icon) {
          const formData = new FormData()
          formData.append('icon', icon)

          // Use admin endpoint for icon upload if in admin context
          const iconUrl = isAdminContext
            ? `${HOST}/admin/templates/${newTemplate.id}/upload-icon`
            : `${HOST}/project/${template.project_id}/templates/${newTemplate.id}/upload-icon`

          const response = await API({
            method: 'PUT',
            url: iconUrl,
            data: formData
          })

          this.addReportTemplate(response.data)
        } else {
          this.addReportTemplate(newTemplate)
        }

      } catch (e) {
        toast({ title: "Can't create report template", variant: 'destructive' })
      }
    },

    async updateReportTemplate(payload: { project_id?: number | string, template: any, icon?: any }) {
      try {
        const { project_id, template, icon } = payload

        // Use admin endpoint if project_id is undefined, otherwise use project endpoint
        const isAdminContext = project_id === undefined
        const updateUrl = isAdminContext
          ? `${HOST}/admin/templates/${template.id}`
          : `${HOST}/project/${project_id}/templates/${template.id}`

        const response = await API({
          method: 'PUT',
          url: updateUrl,
          data: template
        })

        const newTemplate = response.data

        if (icon) {
          const formData = new FormData()
          formData.append('icon', icon)

          // Use admin endpoint for icon upload if in admin context
          const iconUrl = isAdminContext
            ? `${HOST}/admin/templates/${newTemplate.id}/upload-icon`
            : `${HOST}/project/${project_id}/templates/${newTemplate.id}/upload-icon`

          const response = await API({
            method: 'PUT',
            url: iconUrl,
            data: formData
          })

          this.replaceReportTemplate(response.data)
        } else {
          this.replaceReportTemplate(newTemplate)
        }
      } catch (e) {
        toast({ title: "Can't update report template", variant: 'destructive' })
      }
    },

    async fetchProjectReportTemplates(projectId: number | string) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/project/${projectId}/templates`
        })

        this.reportTemplates = response.data
      } catch (e) {
        toast({ title: "Can't fetch report templates", variant: 'destructive' })
      }
    },

    async fetchAllTemplates(organizationId: number | string | null = null) {
      try {
        if (!organizationId) {
          toast({ title: "No organization selected", variant: 'destructive' })
          return
        }

        const response = await API({
          method: 'GET',
          url: `${HOST}/organization/${organizationId}/templates`
        })

        this.allTemplates = response.data
      } catch (e) {
        toast({ title: "Can't fetch templates", variant: 'destructive' })
      }
    },

    async deleteReportTemplate(payload: { project_id?: number | string, template: any }) {
      try {
        const { project_id, template } = payload

        // Use admin endpoint if project_id is undefined, otherwise use project endpoint
        const isAdminContext = project_id === undefined
        const deleteUrl = isAdminContext
          ? `${HOST}/admin/templates/${template.id}`
          : `${HOST}/project/${project_id}/templates/${template.id}`

        await API({
          method: 'DELETE',
          url: deleteUrl
        })

        this.removeReportTemplate(template)
      } catch (e) {
        toast({ title: "Can't delete report template", variant: 'destructive' })
      }
    },

    // Templates Library Actions

    async fetchSystemTemplatesLibrary(projectId: number | string) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/project/${projectId}/templates/library`
        })

        this.systemTemplatesLibrary = response.data
      } catch (e) {
        toast({ title: "Can't fetch templates library", variant: 'destructive' })
      }
    },

    async attachTemplateToProject({ projectId, template }: { projectId: number | string, template: any }) {
      try {
        await API({
          method: 'POST',
          url: `${HOST}/project/${projectId}/templates/${template.id}/attach`
        })

        // Remove from library and add to project templates
        if (this.systemTemplatesLibrary) {
          this.systemTemplatesLibrary = this.systemTemplatesLibrary.filter(t => t.id !== template.id)
        }
        this.addReportTemplate(template)

        toast({ title: 'Template added to project successfully' })
        return true
      } catch (e: any) {
        const errorMessage = e.response?.data?.detail || "Can't attach template to project"
        toast({ title: errorMessage, variant: 'destructive' })
        return false
      }
    },

    async unattachTemplateFromProject({ projectId, template }: { projectId: number | string, template: any }) {
      try {
        await API({
          method: 'DELETE',
          url: `${HOST}/project/${projectId}/templates/${template.id}/unattach`
        })

        // Remove from project templates
        this.removeReportTemplate(template)

        // If it's a system template, add it back to the library
        if (template.is_system_template) {
          if (this.systemTemplatesLibrary) {
            this.systemTemplatesLibrary.push(template)
          } else {
            this.systemTemplatesLibrary = [template]
          }
        }

        toast({ title: 'Template removed from project successfully' })
        return true
      } catch (e: any) {
        const errorMessage = e.response?.data?.detail || "Can't remove template from project"
        toast({ title: errorMessage, variant: 'destructive' })
        return false
      }
    },
  },
})
